import { countries } from "@/assets/countries";
import {
  createOneLocationMutation,
  LocationsLightDocument,
  LocationSortFields,
  TacticsDocument,
} from "@/graphql/jarvis";
import {
  isEmptyValue,
  latitudeValidateGridWithoutNull,
  longitudeValidateGridWithoutNull,
  validateObjectSelected,
  validateSourceActionGrid,
} from "./customValidations";
import { MultiSelectEditor, MultiSelectRenderer } from "./multi-select";
import { paginator } from "./paginator";
import { impactItems } from "./references";
import {
  defaultVariables,
  postRelationshipSectors,
} from "./relationship-sections";
import { SelectObjectEditor, SelectObjectRenderer } from "@/utils/selectObject";
import { dateFormat, timeFormat } from "@/utils/dateTime";
import { LocationDescriptionRenderer } from "@/utils/location-description-renderer";
import moment from "moment";
import store from "@/stores/main";

function validateStartDates(value, callback) {
  const row = this.row;
  const table = this.instance;
  const currentRowData = table.getSourceDataAtRow(row);
  store.dispatch("actiongrid/checkDateisValid", currentRowData);
  let valueIsValid = true;
  const endDate = currentRowData?.endDateFormatted;
  const startDate = value;
  if (endDate && startDate) {
    const startDateFormatted = moment(startDate, dateFormat);
    const endDateFormatted = moment(endDate, dateFormat);
    if (startDateFormatted.isValid() && endDateFormatted.isValid()) {
      if (startDateFormatted.isAfter(endDateFormatted, 'd')) {
        valueIsValid = false;
      }
    }
  }
  if (startDate) {
    const minDate = moment().subtract(6, "months").toDate(),
      maxDate = moment().add(6, "months").toDate();
    const startDateFormatted = moment(startDate, dateFormat);
    // console.log("minDate", minDate);
    // console.log("startDate", startDate);
    // console.log("startDateFormatted.isBefore(minDate)", startDateFormatted.isBefore(minDate));
    if (startDateFormatted.isAfter(maxDate, 'd') || startDateFormatted.isBefore(minDate, 'd')) {
      valueIsValid = false
    }
  }
  if (!value || value?.length < 1 || !valueIsValid) {
    // start date is required
    callback(false);
  } else {
    callback(true);
  }
}

function validateEndDates(value, callback) {
  const row = this.row;
  const table = this.instance;
  const currentRowData = table.getSourceDataAtRow(row);
  const dateFormatted = currentRowData?.dateFormatted;
  const endDate = value;
  store.dispatch("actiongrid/checkDateisValid", currentRowData);
  if (dateFormatted && endDate) {
    const startDateFormatted = moment(dateFormatted, dateFormat);
    const endDateFormatted = moment(endDate, dateFormat);
    if (startDateFormatted.isValid() && endDateFormatted.isValid()) {
      if (endDateFormatted.isBefore(startDateFormatted, 'd')) {
        callback(false);
        return;
      }
    }
  }
  callback(true);
}

const defaultColumnValues = {
  separator: ";",
  valueKey: "id",
  labelKey: "name",
};

const columns = [
  {
    title: "Status",
    width: "200",
    type: "dropdown",
    data: "status",
    source: [],
  },
  {
    title: `Country <span class='red--text font-weight-black'>*</span>`,
    width: "200",
    validator: validateObjectSelected,
    editor: SelectObjectEditor,
    renderer: SelectObjectRenderer,
    select: {
      config: {
        separator: ";",
        valueKey: "iso2",
        labelKey: "name",
      },
      options: countries,
    },
    data: "location",
  },
  {
    title: "Province/State",
    width: "200",
    editor: SelectObjectEditor,
    renderer: SelectObjectRenderer,
    select: {
      config: {
        separator: ";",
        valueKey: "statecode",
        labelKey: "name",
      },
      options: [],
    },
    data: "province",
  },
  {
    title: "City <span class='red--text font-weight-black'>*</span>",
    width: "200",
    validator: validateObjectSelected,
    editor: SelectObjectEditor,
    renderer: SelectObjectRenderer,
    select: {
      config: {
        ...defaultColumnValues,
        addItem: true,
        query: "locations",
        model: "locations",
        doc: LocationsLightDocument,
        quickAdd: true,
        getVariables: defaultVariables,
        getCreateVariables: (city, country, province) => {
          return {
            input: {
              location: {
                city,
                province,
                country,
              },
            },
          };
        },
        quickAddDoc: createOneLocationMutation,
        createdKey: "createOneLocation",
      },
      options: [],
    },
    data: "city",
  },
  {
    title: "Issues <span class='red--text font-weight-black'>*</span>",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    variables: { paging: paginator() },
    validator: isEmptyValue,
    createdKey: "createOneIssue",
    select: {
      config: {},
      options: [],
    },
    data: "issues",
  },
  {
    title:
      "Event Start Date <span class='red--text font-weight-black'>*</span>",
    type: "date",
    width: "150",
    // validator: isEmptyValue,
    dateFormat: dateFormat,
    correctFormat: true,
    validator: validateStartDates,
    data: "dateFormatted",
    datePickerConfig: {
      // disallow any date earlier than 6 months ago:
      minDate: moment().subtract(6, "months").toDate(),
      // disallow any date 6 months in the future:
      maxDate: moment().add(6, "months").toDate(),
    },
  },
  {
    title: "Event Start Time",
    type: "time",
    timeFormat: timeFormat,
    correctFormat: true,
    data: "timeFormatted",
  },
  {
    title: "Event End Date",
    type: "date",
    width: "150",
    validator: validateEndDates,
    dateFormat: dateFormat,
    correctFormat: true,
    data: "endDateFormatted",
  },
  {
    title: "Event End Time",
    type: "time",
    timeFormat: timeFormat,
    data: "endTimeFormatted",
    correctFormat: true,
  },
  {
    title: "Location Description",
    data: "locationDescription",
    width: "300",
    renderer: LocationDescriptionRenderer,
  },
  {
    title: "Latitude",
    validator: latitudeValidateGridWithoutNull,
    type: "text",
    data: "latitude",
  },
  {
    title: "Longitude",
    type: "text",
    validator: longitudeValidateGridWithoutNull,
    data: "longitude",
  },
  {
    title: "Interested",
    type: "numeric",
    data: "interested",
  },
  {
    title: "Going",
    type: "numeric",
    data: "going",
  },
  {
    title: "Content <span class='red--text font-weight-black'>*</span>",
    width: "400",
    validator: isEmptyValue,
    data: "content",
  },
  {
    title: "Hashtags",
    data: "hashtags",
    width: "200",
  },
  {
    title: "Sources",
    validator: validateSourceActionGrid,
    data: "sources",
    width: "200",
  },
  {
    title: "Sectors <span class='red--text font-weight-black'>*</span>",
    width: "200",
    type: "text",
    validator: isEmptyValue,
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "sectors",
  },
  {
    title: "Campaigns",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "campaigns",
  },
  {
    title: "Groups",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "groups",
  },
  {
    title: "Tactic <span class='red--text font-weight-black'>*</span>",
    width: "200",
    validator: isEmptyValue,
    editor: SelectObjectEditor,
    renderer: SelectObjectRenderer,
    select: {
      config: {
        ...defaultColumnValues,
        query: "tactics",
        model: "tactics",
        doc: TacticsDocument,
        quickAdd: false,
        getVariables: defaultVariables,
      },
      options: [],
    },
    data: "tactic",
  },
  {
    title: "Impact",
    width: "100",
    type: "dropdown",
    data: "impact",
    source: impactItems,
  },
  {
    title: "Targets",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "companies",
  },
  {
    title: "Of Interest To",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "ofInterestTo",
  },
  {
    title: "Events",
    width: "200",
    type: "text",
    editor: MultiSelectEditor,
    renderer: MultiSelectRenderer,
    select: {
      config: {},
      options: [],
    },
    data: "events",
  },
  {
    title: "Internal Comments",
    width: 150,
    data: "internalComment",
  },
  {
    title: "Title",
    width: "300",
    data: "title",
    allowEmpty: false,
  },
  // {
  //   title: "Prominent Individuals",
  //   width: "200",
  //   type: "text",
  //   editor: MultiSelectEditor,
  //   renderer: MultiSelectRenderer,
  //   select: {
  //     config: {},
  //     options: [],
  //   },
  //   data: "prominentIndividuals",
  // },
  // {
  //   title: "Of Interest To",
  //   width: "200",
  //   type: "text",
  //   editor: MultiSelectEditor,
  //   renderer: MultiSelectRenderer,
  //   select: {
  //     config: {},
  //     options: [],
  //   },
  //   data: "ofInterestTo",
  // },
  // {
  //   title: "Events",
  //   width: "200",
  //   type: "text",
  //   editor: MultiSelectEditor,
  //   renderer: MultiSelectRenderer,
  //   select: {
  //     config: {},
  //     options: [],
  //   },
  //   data: "events",
  // },
  // {
  //   title: "Audiences <span class='red--text font-weight-black'>*</span>",
  //   width: "200",
  //   type: "text",
  //   validator: isEmptyValue,
  //   editor: MultiSelectEditor,
  //   renderer: MultiSelectRenderer,
  //   select: {
  //     config: {
  //       ...defaultColumnValues
  //     },
  //     options: [],
  //   },
  //   data: "audiences",
  // },
  {
    title: "Action Status",
    width: "200",
    type: "dropdown",
    data: "actionStatus",
    source: [],
  },
];

export const cleanGridSettingsColumn = () => {
  for (const item of columns) {
    const data = item.data ?? null;
    const columnIndex = postRelationshipSectors
      .map((x) => x.model)
      .indexOf(data);
    if (!data || columnIndex < 0) continue;
    const columnItem = postRelationshipSectors[columnIndex];
    const config = {
      ...defaultColumnValues,
      ...columnItem,
    };
    Object.assign(item.select, {
      config,
    });
  }
  return columns;
};
